import React, { Fragment, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import { MessageCircle } from "lucide-react";
import { supabase } from "../supabaseClient";

type TeamOption = { id: string; team_name: string | null };

export default function CoachChatFab() {
  const location = useLocation();
  const [teams, setTeams] = useState<TeamOption[]>([]);

  useEffect(() => {
    let cancelled = false;

    const load = async (userId: string | undefined) => {
      if (!userId) {
        if (!cancelled) setTeams([]);
        return;
      }
      const { data } = await supabase
        .from("teams")
        .select("id, team_name")
        .or("archived.is.null,archived.eq.false")
        .order("team_name", { ascending: true });
      if (!cancelled) setTeams(data ?? []);
    };

    supabase.auth.getUser().then(({ data: { user } }) => load(user?.id));

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      load(session?.user?.id);
    });

    return () => {
      cancelled = true;
      listener.subscription.unsubscribe();
    };
  }, []);

  if (teams.length === 0) return null;

  const isOnChatPage = teams.some((team) =>
    location.pathname.startsWith(`/myteam/${team.id}/messages`)
  );
  if (isOnChatPage) return null;

  const fabClass =
    "fixed z-40 bottom-[calc(env(safe-area-inset-bottom)+16px)] right-4 inline-flex items-center justify-center h-14 w-14 rounded-full bg-orange-500 text-white shadow-lg hover:bg-orange-600 transition";

  if (teams.length === 1) {
    return (
      <Link
        to={`/myteam/${teams[0].id}/messages`}
        aria-label="Messagerie d'équipe"
        title="Messagerie d'équipe"
        className={fabClass}
      >
        <MessageCircle className="h-6 w-6" />
      </Link>
    );
  }

  return (
    <Menu as="div" className="relative">
      <Menu.Button
        aria-label="Messagerie d'équipe"
        title="Messagerie d'équipe"
        className={fabClass}
      >
        <MessageCircle className="h-6 w-6" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className="fixed z-40 bottom-[calc(env(safe-area-inset-bottom)+80px)] right-4
          w-56 max-h-[60vh] overflow-y-auto rounded-xl bg-white shadow-xl ring-1 ring-black/5
          dark:bg-gray-900 dark:ring-white/10 focus:outline-none"
        >
          <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800">
            Messagerie
          </div>
          {teams.map((team) => (
            <Menu.Item key={team.id}>
              {({ active }) => (
                <Link
                  to={`/myteam/${team.id}/messages`}
                  className={`flex items-center gap-2 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 transition ${
                    active ? "bg-gray-100 dark:bg-gray-800" : ""
                  }`}
                >
                  <MessageCircle className="h-4 w-4 text-orange-500 shrink-0" />
                  <span className="truncate">{team.team_name || "Équipe"}</span>
                </Link>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
